import { useLocation } from 'react-router-dom'
import { Slider, SliderNav, useSeo } from '@kkf/design'
import { PROJECT_DETAIL } from '../data/project-details'
import { SITE } from '../data/seo'
import { useT } from '../i18n'

/** The project-details page. */
export function ProjectDetailsPage() {
  const t = useT()
  const d = PROJECT_DETAIL
  const { pathname } = useLocation()

  /*
   * The one route the site tree cannot enumerate: `/projects/:slug` has no slugs. The
   * four projects on `/projects` all link out to another app's domain, so this page is
   * the template's, reachable only by typing a URL. It is described so the tab is not the
   * previous page's, and `noindex` so the template's copy is not what the Foundation
   * ranks for. Give it real content and it belongs in `data/seo.ts` like everything else.
   */
  useSeo(SITE, {
    title: 'Project details — King of Kings Foundation',
    description:
      'One of the Foundation’s projects in detail: what it is, who it serves, and what it needs.',
    path: pathname,
    noindex: true,
  })

  return (
    <>
      {/* No banner and no breadcrumb (2026-09-21) — see `ContactPage` for the two things
          that travel with dropping one: the page's `<h1>`, which `Breadcrumb` used to
          carry, and `header.plainRoutes`, which nothing fails without. */}
      <div className="ul-container ul-section-spacing ul-plain-page-top">
        <Slider
          className="ul-project-details-img-slider"
          options={{
            slidesPerView: 1,
            loop: true,
            autoplay: true,
            navigation: {
              prevEl: '.ul-project-details-slider-nav .prev',
              nextEl: '.ul-project-details-slider-nav .next',
            },
          }}
          slides={d.gallery.map((src, i) => (
            <div key={i}>
              <img src={src} alt={t('alt.generic')} />
            </div>
          ))}
        >
          <SliderNav className="ul-project-details-slider-nav" />
        </Slider>

        <div className="row gx-5 gy-4 flex-column-reverse flex-lg-row">
          {/* event details content */}
          <div className="col-md-8">
            <div className="ul-event-details">
              {/* The page's one `<h1>` — it was an `<h2>` under `Breadcrumb`'s title. */}
              <h1 className="ul-event-details-title">{d.title}</h1>
              <p className="ul-event-details-descr">{d.descr}</p>
              <p>{d.body}</p>
              <h3 className="ul-event-details-inner-title">{d.challengeTitle}</h3>
              <p className="ul-event-details-descr">{d.challengeBody}</p>
              <h3 className="ul-event-inner-title">{d.resultTitle}</h3>
              {/* This copy ends with a literal `./p>` — placeholder text that reads like
                  a broken tag. Replace it with real copy rather than editing it in place. */}
              <p>{d.resultBody}</p>
            </div>
          </div>

          {/* sidebar */}
          <div className="col-md-4">
            <div className="ul-project-details-infos">
              <h4 className="ul-project-details-infos-title">{t('project.information')}</h4>
              <ul className="ul-project-details-infos-list">
                {d.info.map((row) => (
                  <li key={row.key}>
                    <span className="key">{row.key}</span>:<span className="value">{row.value}</span>
                  </li>
                ))}
              </ul>
              <div className="ul-footer-socials ul-project-details-infos-shares">
                {d.socials.map((s) => (
                  <a key={s.icon} href={s.href} aria-label={s.label ?? s.icon}>
                    <i className={`flaticon-${s.icon}`} />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="ul-project-details-nav">
          <a href="#">
            <i className="flaticon-back" /> <span>{t('project.previous')}</span>
          </a>
          <a href="#">
            <i className="flaticon-next" /> <span>{t('project.next')}</span>
          </a>
        </div>
      </div>
    </>
  )
}
